import React, { useCallback, useEffect, useState } from 'react';
import {
  apiListProviders, apiCreateProvider, apiUpdateProvider, apiProviderAction,
  apiDeleteProvider, apiImportEnvProviders, apiProviderLogs,
  ManagedProvider, ProviderLogEntry, PoolStatusItem,
} from '../../services/server-api';
import './AIProviderAdmin.css';

// AI接口管理（仅管理员）：Managed Provider Store 的增删改查 / 启停 / 优先级 / 测试连接
// Key 只展示末 4 位；编辑时留空 = 保留原 Key

const STATUS_META: Record<string, { label: string; cls: string }> = {
  AVAILABLE: { label: '正常', cls: 'st-ok' },
  COOLDOWN: { label: '冷却中', cls: 'st-cool' },
  QUOTA_EXHAUSTED: { label: '额度已满', cls: 'st-quota' },
  CONFIG_ERROR: { label: '配置异常', cls: 'st-err' },
  DISABLED: { label: '已停用', cls: 'st-off' },
};
const statusMeta = (s: string) => STATUS_META[s] || { label: s || '未知', cls: 'st-off' };

const fmtTime = (iso: string | null) => (iso ? iso.slice(5, 16).replace('T', ' ') : '-');

const AIProviderAdmin: React.FC = () => {
  const [providers, setProviders] = useState<ManagedProvider[]>([]);
  const [pool, setPool] = useState<PoolStatusItem[]>([]);
  const [mode, setMode] = useState<'managed' | 'env'>('env');
  const [masterKeyOk, setMasterKeyOk] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [testingId, setTestingId] = useState<string | null>(null);
  const [testResults, setTestResults] = useState<Record<string, { ok: boolean; message: string }>>({});
  const [logs, setLogs] = useState<ProviderLogEntry[]>([]);
  const [logsOpen, setLogsOpen] = useState(false);

  // 新增表单
  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [fName, setFName] = useState('');
  const [fBaseUrl, setFBaseUrl] = useState('https://ark.cn-beijing.volces.com/api/v3');
  const [fModel, setFModel] = useState('');
  const [fApiKey, setFApiKey] = useState('');
  const [fCooldown, setFCooldown] = useState('300');

  const flash = (msg: string) => {
    setNotice(msg);
    setTimeout(() => setNotice(null), 3500);
  };

  const load = useCallback(async () => {
    try {
      const result = await apiListProviders();
      setProviders(result.providers);
      setPool(result.pool);
      setMode(result.mode);
      setMasterKeyOk(result.master_key_configured);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : '加载接口列表失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const loadLogs = async () => {
    try {
      const result = await apiProviderLogs();
      setLogs(result.logs);
    } catch { /* 忽略 */ }
  };

  const openCreate = () => {
    setEditingId(null);
    setFName(''); setFBaseUrl('https://ark.cn-beijing.volces.com/api/v3');
    setFModel(''); setFApiKey(''); setFCooldown('300');
    setFormOpen(true);
  };

  const openEdit = (p: ManagedProvider) => {
    setEditingId(p.id);
    setFName(p.name); setFBaseUrl(p.base_url); setFModel(p.model);
    setFApiKey(''); setFCooldown(String(p.quota_cooldown_seconds));
    setFormOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const payload = {
        name: fName.trim(),
        base_url: fBaseUrl.trim(),
        model: fModel.trim(),
        api_key: fApiKey.trim(),
        quota_cooldown_seconds: Math.max(1, Math.floor(Number(fCooldown) || 300)),
      };
      if (editingId) {
        if (!payload.api_key) delete (payload as { api_key?: string }).api_key;
        await apiUpdateProvider(editingId, payload);
        flash('接口已更新');
      } else {
        await apiCreateProvider(payload);
        flash(`接口「${payload.name}」已新增，默认排在队列最后`);
      }
      setFormOpen(false);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : '保存失败');
    } finally {
      setBusy(false);
    }
  };

  const handleToggle = async (p: ManagedProvider) => {
    setBusy(true);
    try {
      await apiProviderAction(p.id, p.enabled ? 'disable' : 'enable');
      flash(p.enabled ? `「${p.name}」已停用` : `「${p.name}」已启用`);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : '操作失败');
    } finally { setBusy(false); }
  };

  const handleMove = async (p: ManagedProvider, dir: 'up' | 'down') => {
    setBusy(true);
    try {
      await apiProviderAction(p.id, dir);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : '操作失败');
    } finally { setBusy(false); }
  };

  const handleTest = async (p: ManagedProvider) => {
    setTestingId(p.id);
    try {
      const r = await apiProviderAction(p.id, 'test');
      setTestResults(prev => ({ ...prev, [p.id]: { ok: !!r.ok, message: r.message || (r.ok ? '连接成功' : '测试失败') } }));
    } catch (err) {
      setTestResults(prev => ({ ...prev, [p.id]: { ok: false, message: err instanceof Error ? err.message : '测试失败' } }));
    } finally { setTestingId(null); }
  };

  const handleDelete = async (p: ManagedProvider) => {
    if (p.enabled && providers.filter(x => x.enabled).length <= 1) {
      if (!window.confirm(`「${p.name}」是当前最后一个可用 AI 接口，删除后 AI 功能将不可用。确认删除？`)) return;
    } else if (!window.confirm(`确认删除接口「${p.name}」？`)) return;
    setBusy(true);
    try {
      await apiDeleteProvider(p.id);
      flash(`接口「${p.name}」已删除`);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : '删除失败');
    } finally { setBusy(false); }
  };

  const handleImportEnv = async () => {
    if (!window.confirm('将当前环境变量 Provider（AI_*）加密导入托管列表，之后正式切换为托管模式。确认导入？')) return;
    setBusy(true);
    try {
      const r = await apiImportEnvProviders();
      flash(`已导入 ${r.imported} 个环境变量接口，正式切换为托管模式`);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : '导入失败');
    } finally { setBusy(false); }
  };

  const statusOf = (p: ManagedProvider) => statusMeta(p.status).label;

  return (
    <div className="provider-admin">
      <div className="pa-header">
        <div>
          <h2>AI接口管理</h2>
          <p className="pa-sub">
            {mode === 'managed'
              ? '当前为托管模式：调度顺序按下方优先级，主接口恢复后自动切回。'
              : '当前为环境变量模式（后台列表为空时生效）。可一键导入现有配置切换为托管模式。'}
          </p>
        </div>
        <div className="pa-actions">
          <button className="pa-btn" onClick={() => { setLogsOpen(o => !o); if (!logsOpen) loadLogs(); }}>
            调用日志
          </button>
          {mode === 'env' && (
            <button className="pa-btn" onClick={handleImportEnv} disabled={busy || !masterKeyOk}
              title={masterKeyOk ? '' : '服务器尚未配置接口加密主密钥'}>
              导入当前接口配置
            </button>
          )}
          <button className="pa-btn pa-btn-primary" onClick={openCreate} disabled={!masterKeyOk}
            title={masterKeyOk ? '' : '服务器尚未配置接口加密主密钥'}>
            新增接口
          </button>
        </div>
      </div>

      {!masterKeyOk && (
        <div className="pa-warning">
          服务器尚未配置接口加密主密钥（AI_PROVIDER_MASTER_KEY），无法新增/导入/编辑接口密钥。现有环境变量 Provider 不受影响。
        </div>
      )}
      {notice && <div className="pa-notice">{notice}</div>}
      {error && <div className="pa-error">{error}</div>}

      {formOpen && (
        <form className="pa-form" onSubmit={handleSubmit}>
          <h3>{editingId ? '编辑接口' : '新增接口'}</h3>
          <div className="pa-form-grid">
            <label>接口名称
              <input value={fName} onChange={e => setFName(e.target.value)} placeholder="如：ARK主接口" required />
            </label>
            <label>模型 / Endpoint ID
              <input value={fModel} onChange={e => setFModel(e.target.value)} placeholder="如：glm-5.3-flash" required />
            </label>
            <label className="pa-span2">Base URL
              <input value={fBaseUrl} onChange={e => setFBaseUrl(e.target.value)} placeholder="https://ark.cn-beijing.volces.com/api/v3" required />
            </label>
            <label>API Key {editingId && <span className="pa-muted">（留空保留原 Key）</span>}
              <input type="password" value={fApiKey} onChange={e => setFApiKey(e.target.value)}
                placeholder={editingId ? '••••••••' : 'sk-...'} autoComplete="new-password" required={!editingId} />
            </label>
            <label>额度冷却时间（秒，如 18000 = 5小时）
              <input type="number" min={1} value={fCooldown} onChange={e => setFCooldown(e.target.value)} />
            </label>
          </div>
          <div className="pa-form-foot">
            <button type="button" className="pa-btn" onClick={() => setFormOpen(false)}>取消</button>
            <button type="submit" className="pa-btn pa-btn-primary" disabled={busy}>
              {busy ? '保存中...' : editingId ? '保存修改' : '新增（排到队列最后）'}
            </button>
          </div>
        </form>
      )}

      <div className="pa-table-wrap">
        <table className="pa-table">
          <thead>
            <tr>
              <th>优先级</th>
              <th>接口名称</th>
              <th>类型</th>
              <th>模型</th>
              <th>Key尾号</th>
              <th>状态</th>
              <th>最近成功</th>
              <th>最近错误</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={9} className="pa-empty">加载中...</td></tr>}
            {!loading && providers.length === 0 && (
              <tr><td colSpan={9} className="pa-empty">
                托管列表为空，当前使用环境变量 Provider（AI_* / AI_B_* / AI_C_*）。
              </td></tr>
            )}
            {providers.map(p => {
              const meta = statusMeta(p.status);
              const tr = testResults[p.id];
              return (
                <tr key={p.id} className={p.enabled ? '' : 'pa-row-off'}>
                  <td className="pa-priority">{p.priority}</td>
                  <td>
                    <b>{p.name}</b>
                    <div className="pa-url">{p.base_url}</div>
                    {tr && <div className={tr.ok ? 'pa-test-ok' : 'pa-test-fail'}>{tr.ok ? '✓ ' : '✗ '}{tr.message}</div>}
                  </td>
                  <td>{p.provider_type}</td>
                  <td>{p.model}</td>
                  <td className="pa-key">****{p.key_last4 || '----'}</td>
                  <td>
                    <span className={`pa-status ${meta.cls}`}>{statusOf(p)}</span>
                    {p.cooldown_until_iso && <div className="pa-url">恢复 ~{p.cooldown_until_iso.slice(11, 16)}</div>}
                  </td>
                  <td>{fmtTime(p.last_success_at)}</td>
                  <td>{p.last_error_type || '-'}</td>
                  <td className="pa-ops">
                    <button className="pa-mini" onClick={() => handleTest(p)} disabled={testingId === p.id}>
                      {testingId === p.id ? '测试中...' : '测试连接'}
                    </button>
                    <button className="pa-mini" onClick={() => handleToggle(p)} disabled={busy}>
                      {p.enabled ? '停用' : '启用'}
                    </button>
                    <button className="pa-mini" onClick={() => handleMove(p, 'up')} disabled={busy || p.priority <= 1}>上移</button>
                    <button className="pa-mini" onClick={() => handleMove(p, 'down')} disabled={busy || p.priority >= providers.length}>下移</button>
                    <button className="pa-mini" onClick={() => openEdit(p)} disabled={!masterKeyOk}>编辑</button>
                    <button className="pa-mini pa-mini-danger" onClick={() => handleDelete(p)} disabled={busy}>删除</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {pool.length > 0 && (
        <div className="pa-pool">
          <h3>运行时 Pool 状态（当前实际生效）</h3>
          <div className="pa-pool-list">
            {pool.map(item => {
              const meta = statusMeta(item.status);
              return (
                <div key={item.id} className="pa-pool-item">
                  <span className={`pa-status ${meta.cls}`}>{meta.label}</span>
                  <b>{item.name}</b>
                  <span className="pa-muted">P{item.priority} · {item.model}</span>
                  {item.last_error_type && <span className="pa-test-fail">{item.last_error_type}</span>}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {logsOpen && (
        <div className="pa-logs">
          <h3>Provider 调用日志（最近 {logs.length} 条，不含 Key/Prompt）</h3>
          <button className="pa-btn" onClick={loadLogs}>刷新</button>
          <table className="pa-table pa-log-table">
            <thead>
              <tr><th>时间</th><th>接口</th><th>模型</th><th>状态</th><th>耗时</th><th>fallback</th><th>错误类型</th></tr>
            </thead>
            <tbody>
              {logs.length === 0 && <tr><td colSpan={7} className="pa-empty">暂无调用记录（重启后清空）</td></tr>}
              {logs.map((l, i) => (
                <tr key={i}>
                  <td>{l.time.slice(11, 19)}</td>
                  <td>{l.provider_name}</td>
                  <td>{l.model}</td>
                  <td>{l.status}</td>
                  <td>{l.latency}ms</td>
                  <td>{l.fallback}</td>
                  <td>{l.error_type || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AIProviderAdmin;
